import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useAuth } from '../components/auth-context'
import type { AuthWorkspace } from './auth'

export const ACTIVE_WORKSPACE_KEY = 'golf.activeWorkspaceKey'
const WORKSPACE_CHANGED_EVENT = 'golf:active-workspace-changed'

function activeWorkspaceStorageKey(orgId?: string | null): string {
  return orgId ? `${ACTIVE_WORKSPACE_KEY}.${orgId}` : ACTIVE_WORKSPACE_KEY
}

export function readActiveWorkspaceKey(orgId?: string | null): string {
  if (typeof window === 'undefined') return ''
  try {
    return window.localStorage.getItem(activeWorkspaceStorageKey(orgId)) || ''
  } catch {
    return ''
  }
}

export function writeActiveWorkspaceKey(key: string, orgId?: string | null): void {
  if (typeof window === 'undefined') return
  try {
    const storageKey = activeWorkspaceStorageKey(orgId)
    if (key) window.localStorage.setItem(storageKey, key)
    else window.localStorage.removeItem(storageKey)
  } catch {
    void 0
  }
  window.dispatchEvent(new CustomEvent(WORKSPACE_CHANGED_EVENT, { detail: { key, orgId } }))
}

export function useActiveWorkspace(): {
  activeWorkspace: AuthWorkspace | null
  activeWorkspaceKey: string
  setActiveWorkspaceKey: (key: string) => void
} {
  const session = useAuth()
  const orgId = session.organization?.id || ''
  const storedKey = useSyncExternalStore(
    subscribeToWorkspaceStorage,
    () => readActiveWorkspaceKey(orgId),
    () => '',
  )
  const workspaceKeys = useMemo(() => new Set(session.workspaces.map((workspace) => workspace.key)), [session.workspaces])
  const fallbackKey = session.activeWorkspace?.key || session.workspaces[0]?.key || ''
  const activeWorkspaceKey = storedKey && workspaceKeys.has(storedKey) ? storedKey : fallbackKey
  const activeWorkspace = session.workspaces.find((workspace) => workspace.key === activeWorkspaceKey) ?? null

  const setActiveWorkspaceKey = useCallback((key: string) => {
    writeActiveWorkspaceKey(key, orgId)
  }, [orgId])

  return { activeWorkspace, activeWorkspaceKey, setActiveWorkspaceKey }
}

function subscribeToWorkspaceStorage(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener('storage', callback)
  window.addEventListener(WORKSPACE_CHANGED_EVENT, callback)
  return () => {
    window.removeEventListener('storage', callback)
    window.removeEventListener(WORKSPACE_CHANGED_EVENT, callback)
  }
}
